import update from 'immutability-helper';
import ActionTypes from 'constants/ActionTypes';
import concat from 'ramda/src/concat';
import append from 'ramda/src/append';
import isEmpty from 'ramda/src/isEmpty';
import isNil from 'ramda/src/isNil';
import last from 'ramda/src/last';

const initialState = {
    id: '',
    ctr: [],
    data: [],
    error: {
        status: null,
        text: null,
    },
    fetching: false,
    fetchingMore: false,
    nextPage: 0,
    pageBreakpoints: [],
    resultCount: 0,
    seoCompetition: 0,
    seoCompetitionUpdatedAt: 0,
    serpFeaturesImpact: 0,
    updatedAt: 0,
    serpSnapshotIds: [],
    serpSourceUrls: [],
    serpSrcs: [],
};

// A page the refresh could not re-parse is null (ITRBU-709). It keeps the version already in the
// table, so a refresh never takes a page away from it.
const keepPagesNotRefreshed = (state, refreshedPages) =>
    refreshedPages.map((page, index) => {
        if (!isNil(page)) {
            return page;
        }

        const start = index === 0 ? 0 : state.pageBreakpoints[index - 1];
        const end = state.pageBreakpoints[index];
        const results = isNil(start) || isNil(end) ? [] : state.data.slice(start, end);

        return {
            // as the API counts a page's CTRs: the CTR of every result that has one
            ctr: results.map(result => result.ctr).filter(ctr => ctr),
            results,
            serpSnapshotId: state.serpSnapshotIds[index] || '',
            serpSourceUrl: state.serpSourceUrls[index] || '',
            serpSrc: state.serpSrcs[index] || null,
        };
    });

const resultReducer = (state = initialState, action) => {
    switch (action.type) {
        case ActionTypes.DATA_RESULTS_FETCHING: {
            return update(state, {
                fetching: { $set: true },
            });
        }
        case ActionTypes.DATA_RESULTS_RECEIVED: {
            return update(state, {
                id: { $set: action.payload.id },
                ctr: { $set: action.payload.ctr },
                data: { $set: action.payload.results },
                error: {
                    status: { $set: initialState.error.status },
                    text: { $set: initialState.error.text },
                },
                fetching: { $set: false },
                nextPage: { $set: 1 },
                pageBreakpoints: { $set: [action.payload.results.length] },
                resultCount: { $set: action.payload.resultCount },
                seoCompetition: { $set: action.payload.seoCompetition },
                seoCompetitionUpdatedAt: { $set: action.payload.seoCompetitionUpdatedAt },
                serpFeaturesImpact: { $set: action.payload.serpFeaturesImpact },
                updatedAt: { $set: action.payload.updatedAt },
                serpSnapshotIds: { $set: [action.payload.serpSnapshotId] },
                serpSourceUrls: { $set: [action.payload.serpSourceUrl] },
                serpSrcs: { $set: [action.payload.serpSrc] },
            });
        }
        case ActionTypes.DATA_RESULTS_RESET_RECEIVED: {
            const refreshedPages = action.payload.pages;
            const pages = keepPagesNotRefreshed(state, refreshedPages);
            const firstPage = refreshedPages[0];
            // The keyword-level numbers come with the first page; a first page that was not
            // refreshed keeps the ones on screen
            const firstPageUpdates = isNil(firstPage)
                ? {}
                : {
                      id: { $set: firstPage.id },
                      resultCount: { $set: firstPage.resultCount },
                      seoCompetition: { $set: firstPage.seoCompetition },
                      seoCompetitionUpdatedAt: { $set: firstPage.seoCompetitionUpdatedAt },
                      serpFeaturesImpact: { $set: firstPage.serpFeaturesImpact },
                      updatedAt: { $set: firstPage.updatedAt },
                  };

            // Every list holds one entry per page, as after loading the pages one by one
            return update(state, {
                ...firstPageUpdates,
                ctr: { $set: pages.reduce((ctr, page) => concat(ctr, page.ctr), []) },
                data: { $set: pages.reduce((data, page) => concat(data, page.results), []) },
                error: {
                    status: { $set: initialState.error.status },
                    text: { $set: initialState.error.text },
                },
                fetching: { $set: false },
                nextPage: { $set: pages.length },
                pageBreakpoints: {
                    $set: pages.reduce(
                        (breakpoints, page) =>
                            append((isEmpty(breakpoints) ? 0 : last(breakpoints)) + page.results.length, breakpoints),
                        [],
                    ),
                },
                serpSnapshotIds: { $set: pages.map(page => page.serpSnapshotId) },
                serpSourceUrls: { $set: pages.map(page => page.serpSourceUrl) },
                serpSrcs: { $set: pages.map(page => page.serpSrc) },
            });
        }
        case ActionTypes.DATA_RESULTS_EMPTY_RECEIVED: {
            return update(state, {
                fetching: { $set: false },
            });
        }
        case ActionTypes.DATA_RESULTS_MORE_EMPTY_RECEIVED: {
            return update(state, {
                fetchingMore: { $set: false },
            });
        }
        case ActionTypes.DATA_RESULTS_ERROR: {
            return update(state, {
                fetching: { $set: false },
                error: {
                    status: { $set: action.payload.status },
                    text: { $set: action.payload.text },
                },
            });
        }
        case ActionTypes.DATA_RESULTS_MORE_FETCHING: {
            return update(state, {
                fetchingMore: { $set: true },
            });
        }
        case ActionTypes.DATA_RESULTS_MORE_RECEIVED: {
            return update(state, {
                serpSnapshotIds: { $apply: data => append(action.payload.serpData.serpSnapshotId, data) },
                serpSourceUrls: { $apply: data => append(action.payload.serpData.serpSourceUrl, data) },
                serpSrcs: { $apply: data => append(action.payload.serpData.serpSrc, data) },
                ctr: { $apply: data => concat(data, action.payload.serpData.ctr) },
                fetchingMore: { $set: false },
                nextPage: { $apply: nextPage => nextPage + 1 },
                data: { $apply: data => concat(data, action.payload.serpData.results) },
                pageBreakpoints: {
                    $apply: breakpoints =>
                        concat(breakpoints, [state.data.length + action.payload.serpData.results.length]),
                },
            });
        }
        case ActionTypes.DATA_RESULTS_MORE_ERROR: {
            return update(state, {
                fetchingMore: { $set: false },
                error: {
                    status: { $set: action.payload.status },
                    text: { $set: action.payload.text },
                },
            });
        }
        default: {
            return state;
        }
    }
};

export default resultReducer;
